import React, { useEffect, useState } from 'react';
import { View, Text, StyleSheet, ActivityIndicator, Platform } from 'react-native';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../services/AuthContext';

/**
 * Auth Callback Screen
 * Handles OAuth redirects from both Cognito Hosted UI and Clerk
 * 
 * Web: Handles /auth/callback and /sso-callback routes
 * Native: Handles chessmaps://auth deep link
 */
const AuthCallbackScreen = ({ navigation }) => {
  const { isDarkMode } = useTheme();
  const { handleOAuthCallback, isSignedIn, loading: authLoading, user } = useAuth();
  const [status, setStatus] = useState('Processing authentication...');
  const [error, setError] = useState(null);
  
  const theme = {
    bg: isDarkMode ? '#0A0A0A' : '#FAFAFA',
    text: isDarkMode ? '#FFFFFF' : '#0A0A0A',
    textSecondary: isDarkMode ? '#888888' : '#666666',
    accent: '#94EB98',
    error: '#FF6B6B',
  };

  useEffect(() => {
    handleCallback();
  }, []);

  const handleCallback = async () => {
    try {
      console.log('🔄 Processing auth callback...');
      console.log('🔄 Current URL:', typeof window !== 'undefined' ? window.location.href : 'N/A');
      
      if (Platform.OS === 'web') {
        // Try to handle Clerk OAuth callback
        // Clerk automatically processes the callback when the page loads
        console.log('🔐 Processing Clerk OAuth callback...');
        setStatus('Completing sign in...');
        
        // Use Clerk's OAuth callback handler
        const result = await handleOAuthCallback();
        
        if (result.success) {
          setStatus('Sign in successful! Redirecting...');
          
          // Wait for Clerk to set the session and AuthContext to sync user
          // Then redirect - AppNavigator will show the correct stack based on auth state
          setTimeout(() => {
            // Redirect to root - AppNavigator will automatically show MainStack or CompleteProfile
            // based on user state and authState.needsCompleteProfile
            window.location.href = '/';
          }, 1500);
          return;
        } else if (result.needsUsername) {
          // New user needs to complete profile (account type, username, etc.)
          // authState.needsCompleteProfile has been set in handleOAuthCallback
          // AppNavigator will show MainStack > CompleteProfileScreen with the registration modal
          // Do NOT redirect - a full reload would lose the Clerk sign-up state
          setStatus('Setting up your profile...');
          return;
        } else {
          // Handle error from Clerk callback
          setError(result.error || 'Authentication failed');
          setStatus('Authentication failed');
          setTimeout(() => {
            window.location.href = '/Login';
          }, 3000);
          return;
        }
        
        // Fallback: Check for Cognito-style callback (old flow)
        const urlParams = new URLSearchParams(window.location.search);
        const errorParam = urlParams.get('error');
        const errorDescription = urlParams.get('error_description');
        const code = urlParams.get('code');
        
        console.log('🔄 URL params:', { errorParam, errorDescription, code: code ? 'present' : 'missing' });
        
        if (errorParam) {
          console.error('❌ OAuth error:', errorParam, errorDescription);
          setError(errorDescription || errorParam);
          setStatus('Authentication failed');
          
          setTimeout(() => {
            window.location.href = '/Login';
          }, 3000);
          return;
        }
        
        if (!code) {
          console.error('❌ No authorization code or Clerk sign-in ID found');
          setError('No authorization code received');
          setStatus('Authentication failed');
          setTimeout(() => {
            window.location.href = '/Login';
          }, 3000);
          return;
        }
        
        // Old Cognito flow (shouldn't happen anymore, but handle gracefully)
        setStatus('Completing sign in...');
        setError('Old authentication method detected. Please use Google/Apple sign-in from the login screen.');
        setTimeout(() => {
          window.location.href = '/Login';
        }, 3000);
        return;
      }
      
      // Native platform handling (if needed)
      setError('Native OAuth callback not yet implemented');
      setTimeout(() => {
        navigation.navigate('Login');
      }, 3000);
      
    } catch (err) {
      console.error('❌ Auth callback error:', err);
      console.error('❌ Error stack:', err.stack);
      setError(err.message || 'Authentication failed');
      setStatus('Authentication failed');
      
      // Redirect to login after error
      if (Platform.OS === 'web') {
        setTimeout(() => {
          window.location.href = '/Login';
        }, 3000);
      } else {
        setTimeout(() => {
          navigation.navigate('Login');
        }, 3000);
      }
    }
  };

  return (
    <View style={[styles.container, { backgroundColor: theme.bg }]}>
      <View style={styles.content}>
        {!error ? (
          <ActivityIndicator size="large" color={theme.accent} />
        ) : (
          <View style={styles.errorIcon}>
            <Text style={{ fontSize: 48 }}>⚠️</Text>
          </View>
        )}
        
        <Text style={[styles.status, { color: theme.text }]}>
          {status}
        </Text>
        
        {error && (
          <Text style={[styles.errorText, { color: theme.error }]}>
            {error}
          </Text>
        )}
        
        <Text style={[styles.subtitle, { color: theme.textSecondary }]}>
          {error ? 'Redirecting to login...' : 'Please wait...'}
        </Text>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  content: {
    alignItems: 'center',
  },
  status: {
    marginTop: 24,
    fontSize: 20,
    fontWeight: '600',
    textAlign: 'center',
  },
  subtitle: {
    marginTop: 12,
    fontSize: 14,
    textAlign: 'center',
  },
  errorIcon: {
    marginBottom: 8,
  },
  errorText: {
    marginTop: 12,
    fontSize: 14,
    textAlign: 'center',
    maxWidth: 300,
  },
});

export default AuthCallbackScreen;
